export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://instaport-e6fnbjbhhtbbc7ca.southindia-01.azurewebsites.net";
export const STORAGE_KEY = "instateam-employee-session";

const DOCUMENT_LABELS = {
  aadhaarCard: "Aadhaar Card",
  panCard: "PAN Card",
  resumeCv: "Resume / CV",
  educationalCertificate: "Educational Certificate",
  otherDocument: "Other Document",
};

export function parseStoredSession() {
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
}

export function getInitials(name) {
  return String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function toAbsoluteUrl(pathname) {
  if (!pathname) return "";
  if (pathname.startsWith("http")) return pathname;
  return `${API_BASE_URL}${pathname}`;
}

export function formatFieldValue(value) {
  if (value === 0) return "0";
  return value ? String(value) : "—";
}

export function formatStatusLabel(value) {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "—";

  return normalizedValue
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function formatDateValue(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatCurrencyValue(value) {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return "—";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatWorkingHoursValue(startTime, endTime, workingHoursLabel = "") {
  if (workingHoursLabel) {
    return workingHoursLabel;
  }

  const safeStart = String(startTime || "").trim();
  const safeEnd = String(endTime || "").trim();

  if (!safeStart || !safeEnd) {
    return "—";
  }

  const startDate = new Date(`2000-01-01T${safeStart}:00`);
  const endDate = new Date(`2000-01-01T${safeEnd}:00`);

  const formatTime = (date, fallbackValue) =>
    Number.isNaN(date.getTime())
      ? fallbackValue
      : new Intl.DateTimeFormat("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date);

  return `${formatTime(startDate, safeStart)} - ${formatTime(endDate, safeEnd)}`;
}

export function formatDocumentLabel(key) {
  return DOCUMENT_LABELS[key] || key;
}

export function getEmployeeDocuments(documents) {
  return Object.entries(documents || {}).filter(([, file]) => file?.url);
}

const EMPLOYEE_THEME_PRESETS = {
  warm: "#ffc21a",
  emerald: "#22c55e",
  ocean: "#3b82f6",
};

const PRESENCE_STATUS_META = {
  online: {
    label: "Online",
    icon: "check",
    className: "online",
  },
  right_back: {
    label: "Right back",
    icon: "clock",
    className: "right_back",
  },
  busy: {
    label: "Busy",
    icon: "",
    className: "busy",
  },
  out_of_office: {
    label: "Out of office",
    icon: "logOut",
    className: "out_of_office",
  },
};

const PROFILE_PRESENCE_KEYS = Object.keys(PRESENCE_STATUS_META);

export function normalizeHexColor(value) {
  const normalized = String(value || "").trim();
  return /^#([0-9a-fA-F]{6})$/.test(normalized) ? normalized.toLowerCase() : "";
}

function hexToRgb(hexColor) {
  const safeColor = normalizeHexColor(hexColor);
  if (!safeColor) return null;

  return {
    red: Number.parseInt(safeColor.slice(1, 3), 16),
    green: Number.parseInt(safeColor.slice(3, 5), 16),
    blue: Number.parseInt(safeColor.slice(5, 7), 16),
  };
}

function mixChannels(baseValue, targetValue, ratio) {
  return Math.round(baseValue + (targetValue - baseValue) * ratio);
}

function rgbToHex({ red, green, blue }) {
  return `#${[red, green, blue]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function lightenColor(hexColor, ratio) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#ffc21a";

  return rgbToHex({
    red: mixChannels(rgb.red, 255, ratio),
    green: mixChannels(rgb.green, 255, ratio),
    blue: mixChannels(rgb.blue, 255, ratio),
  });
}

function darkenColor(hexColor, ratio) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#f5b700";

  return rgbToHex({
    red: mixChannels(rgb.red, 0, ratio),
    green: mixChannels(rgb.green, 0, ratio),
    blue: mixChannels(rgb.blue, 0, ratio),
  });
}

export function resolveEmployeeAccentColor(settings = {}) {
  const themeKey = String(settings?.theme || "warm").trim();
  if (themeKey === "custom") {
    return normalizeHexColor(settings?.customAccentColor) || EMPLOYEE_THEME_PRESETS.warm;
  }

  return EMPLOYEE_THEME_PRESETS[themeKey] || EMPLOYEE_THEME_PRESETS.warm;
}

export function applyEmployeeTheme(settings = {}) {
  if (typeof document === "undefined") return;

  const accent = resolveEmployeeAccentColor(settings);
  const rgb = hexToRgb(accent) || { red: 255, green: 194, blue: 26 };
  const root = document.documentElement;

  root.style.setProperty("--employee-brand", accent);
  root.style.setProperty("--employee-yellow", accent);
  root.style.setProperty("--employee-brand-rgb", `${rgb.red}, ${rgb.green}, ${rgb.blue}`);
  root.style.setProperty("--employee-brand-top", lightenColor(accent, 0.2));
  root.style.setProperty("--employee-brand-soft", lightenColor(accent, 0.82));
  root.style.setProperty("--employee-brand-soft-strong", lightenColor(accent, 0.6));
  root.style.setProperty("--employee-brand-deep", darkenColor(accent, 0.08));
  root.style.setProperty("--employee-brand-strong", darkenColor(accent, 0.16));
  root.style.setProperty("--employee-brand-text", darkenColor(accent, 0.38));
}

export function getPresenceStatusMeta(status) {
  const safeStatus = String(status || "online").trim();
  return PRESENCE_STATUS_META[safeStatus] || PRESENCE_STATUS_META.online;
}

export function resolveEmployeeProfileStatus(source = {}) {
  const directProfileStatus = String(source?.profileStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(directProfileStatus)) {
    return directProfileStatus;
  }

  const settingsPresenceStatus = String(source?.settings?.presenceStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(settingsPresenceStatus)) {
    return settingsPresenceStatus;
  }

  const directPresenceStatus = String(source?.presenceStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(directPresenceStatus)) {
    return directPresenceStatus;
  }

  return "online";
}
